// [SUPERADMIN-PAIEMENTS-V2] - Page Paiements avec nouveau modèle tarifaire
// [MIGRATION-MONGODB] - Migré de localStorage vers API/MongoDB
import { useState, useEffect, useMemo } from "react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";
import {
  CreditCard,
  TrendingUp,
  Download,
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  MoreVertical,
  DollarSign,
  Users,
  Repeat,
  X,
  FileText,
  Wallet
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { COLORS } from "../components/SuperAdminLayout";

// [SUPERADMIN-PAIEMENTS-V2] Constantes du nouveau modèle
const ONESHOT_PRICE = 485;
const MAINTENANCE_PRICE = 30;
const EXTRA_USER_PRICE = 30;
const DEFAULT_CURRENCY = "USD";

// [SUPERADMIN-PAIEMENTS-V2] Modes de paiement
const PAYMENT_MODES = [
  { value: "virement", label: "Virement" },
  { value: "especes", label: "Espèces" },
  { value: "mobile_money", label: "Mobile Money" },
  { value: "autre", label: "Autre" },
];

// [SUPERADMIN-PAIEMENTS-V2] Modale Enregistrer un Paiement
const PaymentModal = ({ isOpen, onClose, boutique, paymentType, onSave }) => {
  const [montant, setMontant] = useState("");
  const [devise, setDevise] = useState(DEFAULT_CURRENCY);
  const [datePaiement, setDatePaiement] = useState(format(new Date(), "yyyy-MM-dd"));
  const [modePaiement, setModePaiement] = useState("virement");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setMontant(paymentType === "one-shot" ? ONESHOT_PRICE.toString() : MAINTENANCE_PRICE.toString());
      setDevise(DEFAULT_CURRENCY);
      setDatePaiement(format(new Date(), "yyyy-MM-dd"));
      setModePaiement("virement");
      setNotes("");
    }
  }, [isOpen, paymentType]);

  const handleSubmit = () => {
    if (!montant || parseFloat(montant) <= 0) {
      toast.error("Montant invalide");
      return;
    }
    setSaving(true);
    const payment = {
      boutiqueSlug: boutique?.slug,
      boutiqueName: boutique?.name,
      boutiqueId: boutique?.id,
      type: paymentType,
      montant: parseFloat(montant),
      devise,
      date: new Date(datePaiement).toISOString(),
      modePaiement,
      notes,
    };
    onSave(payment);
    setSaving(false);
    onClose();
  };

  if (!isOpen || !boutique) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="w-full max-w-md rounded-2xl p-6"
          style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}
          onClick={e => e.stopPropagation()}
          data-testid="payment-modal"
        >
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-bold text-lg" style={{ color: COLORS.text }}>Enregistrer un paiement</h2>
            <button onClick={onClose} style={{ color: COLORS.textSecondary }}><X className="w-5 h-5" /></button>
          </div>

          <p className="font-medium mb-4" style={{ color: COLORS.text }}>{boutique.name}</p>

          {/* Type */}
          <div className="mb-4">
            <label className="block text-sm mb-2" style={{ color: COLORS.textSecondary }}>Type de paiement</label>
            <div className="flex gap-2">
              {[{ v: "one-shot", l: `One-shot ${ONESHOT_PRICE}$` }, { v: "maintenance", l: `Maintenance ${MAINTENANCE_PRICE}$` }].map(t => (
                <button
                  key={t.v}
                  className="flex-1 py-2.5 rounded-xl text-sm font-medium transition-all"
                  style={{
                    backgroundColor: paymentType === t.v ? `${COLORS.accent}15` : '#F8FAFC',
                    border: `2px solid ${paymentType === t.v ? COLORS.accent : COLORS.border}`,
                    color: paymentType === t.v ? COLORS.accent : COLORS.textSecondary,
                  }}
                  disabled
                >
                  {t.l}
                </button>
              ))}
            </div>
          </div>

          {/* Montant + Devise */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div>
              <label className="block text-sm mb-2" style={{ color: COLORS.textSecondary }}>Montant</label>
              <input
                type="number"
                value={montant}
                onChange={e => setMontant(e.target.value)}
                className="w-full rounded-xl px-4 py-2.5 focus:outline-none"
                style={{ backgroundColor: '#F8FAFC', border: `1px solid ${COLORS.border}`, color: COLORS.text }}
                data-testid="payment-amount"
              />
            </div>
            <div>
              <label className="block text-sm mb-2" style={{ color: COLORS.textSecondary }}>Devise</label>
              <select
                value={devise}
                onChange={e => setDevise(e.target.value)}
                className="w-full rounded-xl px-4 py-2.5 focus:outline-none appearance-none"
                style={{ backgroundColor: '#F8FAFC', border: `1px solid ${COLORS.border}`, color: COLORS.text }}
              >
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="XOF">XOF</option>
                <option value="CHF">CHF</option>
              </select>
            </div>
          </div>

          {/* Date */}
          <div className="mb-4">
            <label className="block text-sm mb-2" style={{ color: COLORS.textSecondary }}>Date de paiement</label>
            <input
              type="date"
              value={datePaiement}
              onChange={e => setDatePaiement(e.target.value)}
              className="w-full rounded-xl px-4 py-2.5 focus:outline-none"
              style={{ backgroundColor: '#F8FAFC', border: `1px solid ${COLORS.border}`, color: COLORS.text }}
              data-testid="payment-date"
            />
          </div>

          {/* Mode */}
          <div className="mb-4">
            <label className="block text-sm mb-2" style={{ color: COLORS.textSecondary }}>Mode de paiement</label>
            <div className="grid grid-cols-2 gap-2">
              {PAYMENT_MODES.map(m => (
                <button
                  key={m.value}
                  onClick={() => setModePaiement(m.value)}
                  className="py-2 rounded-xl text-sm font-medium transition-all"
                  style={{
                    backgroundColor: modePaiement === m.value ? `${COLORS.accent}15` : '#F8FAFC',
                    border: `1.5px solid ${modePaiement === m.value ? COLORS.accent : COLORS.border}`,
                    color: modePaiement === m.value ? COLORS.accent : COLORS.textSecondary,
                  }}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div className="mb-5">
            <label className="block text-sm mb-2" style={{ color: COLORS.textSecondary }}>Notes (optionnel)</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Référence virement, remarques..."
              rows={2}
              className="w-full rounded-xl px-4 py-2.5 focus:outline-none resize-none text-sm"
              style={{ backgroundColor: '#F8FAFC', border: `1px solid ${COLORS.border}`, color: COLORS.text }}
            />
          </div>

          <Button
            onClick={handleSubmit}
            disabled={saving}
            className="w-full py-3 rounded-xl font-semibold"
            style={{ backgroundColor: COLORS.accent }}
            data-testid="submit-payment-btn"
          >
            {saving ? "Enregistrement..." : "Enregistrer le paiement"}
          </Button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// [SUPERADMIN-PAIEMENTS-V2] Modale Historique Paiements
const HistoryModal = ({ isOpen, onClose, boutique, payments }) => {
  if (!isOpen || !boutique) return null;

  const boutiquePayments = payments.filter(p => 
    (p.boutiqueId === boutique.id) || (p.boutique_id === boutique.id) ||
    (p.boutiqueSlug === boutique.slug) || (p.boutique_slug === boutique.slug)
  );
  const totalEncaisse = boutiquePayments.reduce((s, p) => s + p.montant, 0);

  const exportCSV = () => {
    const header = "Date,Type,Montant,Devise,Mode,Notes\n";
    const rows = boutiquePayments.map(p =>
      `${format(new Date(p.date), "dd/MM/yyyy")},${p.type},${p.montant},${p.devise},${p.mode_paiement || p.modePaiement},"${p.notes || ''}"`
    ).join("\n");
    const blob = new Blob([header + rows], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `paiements-${boutique.slug}.csv`;
    link.click();
    toast.success("CSV exporté");
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="w-full max-w-lg rounded-2xl p-6 max-h-[80vh] overflow-y-auto"
          style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}
          onClick={e => e.stopPropagation()}
          data-testid="history-modal"
        >
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="font-bold text-lg" style={{ color: COLORS.text }}>Historique paiements</h2>
              <p className="text-sm" style={{ color: COLORS.textSecondary }}>{boutique.name}</p>
            </div>
            <button onClick={onClose} style={{ color: COLORS.textSecondary }}><X className="w-5 h-5" /></button>
          </div>

          {boutiquePayments.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="w-10 h-10 mx-auto mb-3" style={{ color: COLORS.textSecondary }} />
              <p style={{ color: COLORS.textSecondary }}>Aucun paiement enregistré</p>
            </div>
          ) : (
            <>
              <div className="space-y-2 mb-4">
                {boutiquePayments.map((p, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between p-3 rounded-xl"
                    style={{ backgroundColor: '#F8FAFC', border: `1px solid ${COLORS.border}` }}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-8 h-8 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: p.type === "one-shot" ? `${COLORS.accent}15` : `${COLORS.positive}15` }}
                      >
                        {p.type === "one-shot"
                          ? <DollarSign className="w-4 h-4" style={{ color: COLORS.accent }} />
                          : <Repeat className="w-4 h-4" style={{ color: COLORS.positive }} />}
                      </div>
                      <div>
                        <p className="text-sm font-medium" style={{ color: COLORS.text }}>
                          {p.type === "one-shot" ? "One-shot" : "Maintenance"}
                        </p>
                        <p className="text-xs" style={{ color: COLORS.textSecondary }}>
                          {format(new Date(p.date), "d MMM yyyy", { locale: fr })} · {PAYMENT_MODES.find(m => m.value === (p.mode_paiement || p.modePaiement))?.label || p.mode_paiement || p.modePaiement}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold" style={{ color: COLORS.text }}>{p.montant} {p.devise}</p>
                      {p.notes && <p className="text-xs truncate max-w-[120px]" style={{ color: COLORS.textSecondary }}>{p.notes}</p>}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-between p-3 rounded-xl mb-4" style={{ backgroundColor: `${COLORS.accent}08`, border: `1px solid ${COLORS.accent}20` }}>
                <span className="font-medium" style={{ color: COLORS.text }}>Total encaissé</span>
                <span className="font-bold text-lg" style={{ color: COLORS.text }}>{totalEncaisse.toLocaleString('fr-FR')} $</span>
              </div>

              <Button
                onClick={exportCSV}
                variant="outline"
                className="w-full gap-2"
                style={{ borderColor: COLORS.border, color: COLORS.text }}
                data-testid="export-csv-btn"
              >
                <Download className="w-4 h-4" />
                Exporter CSV
              </Button>
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// [SUPERADMIN-PAIEMENTS-V2] Composant principal
export default function SuperAdminPayments() {
  const [boutiques, setBoutiques] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  const [paymentModal, setPaymentModal] = useState({ open: false, boutique: null, type: "one-shot" });
  const [historyModal, setHistoryModal] = useState({ open: false, boutique: null });

  // Charger boutiques et paiements depuis l'API
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [boutiquesRes, paymentsRes] = await Promise.allSettled([
          axios.get(`${API}/admin/boutiques`),
          axios.get(`${API}/superadmin/payments`)
        ]);

        if (boutiquesRes.status === "fulfilled") {
          const mapped = boutiquesRes.value.data.map(b => ({
            id: b.id || b.admin_id,
            name: b.boutique_name || b.name || b.slug,
            slug: b.slug,
            users: b.users || 1,
            status: b.status || "active",
            compteValide: b.compteValide === true,
            oneshotPaid: false,
            oneshotDate: null,
            maintenancePaid: false,
            createdAt: b.created_at || new Date().toISOString(),
          }));

          // Enrichir avec les paiements existants
          let paymentsData = [];
          if (paymentsRes.status === "fulfilled") {
            paymentsData = paymentsRes.value.data;
            setPayments(paymentsData);
          }

          const enriched = mapped.map(b => {
            const bPayments = paymentsData.filter(p => p.boutique_id === b.id || p.boutique_slug === b.slug);
            const oneshotPayment = bPayments.find(p => p.type === "one-shot");
            const maintenancePayment = bPayments.find(p => p.type === "maintenance");
            return {
              ...b,
              oneshotPaid: !!oneshotPayment,
              oneshotDate: oneshotPayment?.date || null,
              maintenancePaid: !!maintenancePayment,
            };
          });
          setBoutiques(enriched);
        }
      } catch (e) {
        console.error("Error fetching data:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSavePayment = async (payment) => {
    try {
      // [MIGRATION-MONGODB] Sauvegarder via API
      const res = await axios.post(`${API}/superadmin/payments`, {
        boutique_slug: payment.boutiqueSlug,
        boutique_name: payment.boutiqueName,
        boutique_id: payment.boutiqueId,
        type: payment.type,
        montant: payment.montant,
        devise: payment.devise,
        date: payment.date,
        mode_paiement: payment.modePaiement,
        notes: payment.notes,
      });
      setPayments(prev => [...prev, res.data]);
    } catch (e) {
      console.error("Error saving payment:", e);
      // Fallback: sauvegarder localement quand même
      setPayments(prev => [...prev, payment]);
    }

    // Mettre à jour le statut boutique localement
    setBoutiques(prev => prev.map(b => {
      if (b.id !== payment.boutiqueId) return b;
      if (payment.type === "one-shot") {
        return { ...b, oneshotPaid: true, oneshotDate: format(new Date(payment.date), "yyyy-MM-dd") };
      }
      return { ...b, maintenancePaid: true };
    }));

    toast.success("Paiement enregistré");
  };

  const markOneshotPaid = (boutique) => {
    setPaymentModal({ open: true, boutique, type: "one-shot" });
  };

  const markMaintenancePaid = (boutique) => {
    setPaymentModal({ open: true, boutique, type: "maintenance" });
  };

  // [SUPERADMIN-PAIEMENTS-V2] KPIs
  const kpis = useMemo(() => {
    const oneshotPaidCount = boutiques.filter(b => b.oneshotPaid).length;
    const oneshotTotal = oneshotPaidCount * ONESHOT_PRICE;

    const activeMaintenance = boutiques.filter(b => b.maintenancePaid && b.status === "active");
    const mrrBase = activeMaintenance.length * MAINTENANCE_PRICE;
    const mrrExtraUsers = activeMaintenance.reduce((s, b) => s + Math.max(0, b.users - 1) * EXTRA_USER_PRICE, 0);
    const mrr = mrrBase + mrrExtraUsers;

    const pendingOneshot = boutiques.filter(b => !b.oneshotPaid).length;
    const arr = mrr * 12;

    return { oneshotPaidCount, oneshotTotal, mrr, pendingOneshot, arr };
  }, [boutiques]);

  return (
    <div className="space-y-6" data-testid="superadmin-payments">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: COLORS.text }}>Paiements</h1>
          <p className="text-sm" style={{ color: COLORS.textSecondary }}>
            Nouveau modèle : {ONESHOT_PRICE}$ one-shot + {MAINTENANCE_PRICE}$/mois maintenance
          </p>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Card 1 - Paiements initiaux */}
        <Card className="p-5 shadow-sm" style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#FF450015' }}>
              <DollarSign className="w-5 h-5" style={{ color: '#FF4500' }} />
            </div>
            <span className="text-sm" style={{ color: COLORS.textSecondary }}>Paiements initiaux reçus</span>
          </div>
          <p className="text-2xl font-bold" style={{ color: COLORS.text }}>{kpis.oneshotPaidCount} x {ONESHOT_PRICE}$</p>
          <p className="text-sm mt-1" style={{ color: '#FF4500' }}>= {kpis.oneshotTotal.toLocaleString('fr-FR')}$</p>
        </Card>

        {/* Card 2 - MRR Maintenance */}
        <Card className="p-5 shadow-sm" style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#22C55E15' }}>
              <Repeat className="w-5 h-5" style={{ color: '#22C55E' }} />
            </div>
            <span className="text-sm" style={{ color: COLORS.textSecondary }}>MRR Maintenance</span>
          </div>
          <p className="text-2xl font-bold" style={{ color: COLORS.text }}>{kpis.mrr.toLocaleString('fr-FR')}$/mois</p>
          <p className="text-sm mt-1" style={{ color: '#22C55E' }}>Maintenance + suppléments</p>
        </Card>

        {/* Card 3 - En attente */}
        <Card className="p-5 shadow-sm" style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#F59E0B15' }}>
              <Clock className="w-5 h-5" style={{ color: '#F59E0B' }} />
            </div>
            <span className="text-sm" style={{ color: COLORS.textSecondary }}>Paiements en attente</span>
          </div>
          <p className="text-2xl font-bold" style={{ color: COLORS.text }}>{kpis.pendingOneshot}</p>
          <p className="text-sm mt-1" style={{ color: '#F59E0B' }}>One-shot non reçu</p>
        </Card>

        {/* Card 4 - ARR */}
        <Card className="p-5 shadow-sm" style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#8B5CF615' }}>
              <TrendingUp className="w-5 h-5" style={{ color: '#8B5CF6' }} />
            </div>
            <span className="text-sm" style={{ color: COLORS.textSecondary }}>ARR estimé</span>
          </div>
          <p className="text-2xl font-bold" style={{ color: COLORS.text }}>{kpis.arr.toLocaleString('fr-FR')}$/an</p>
          <p className="text-sm mt-1" style={{ color: '#8B5CF6' }}>MRR x 12</p>
        </Card>
      </div>

      {/* Tableau Paiements */}
      <Card className="overflow-hidden shadow-sm" style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}>
        <div className="hidden lg:grid grid-cols-[2fr,1fr,1fr,1fr,1fr,60px] gap-4 px-6 py-3 text-xs uppercase font-medium"
          style={{ backgroundColor: '#F1F5F9', color: COLORS.textSecondary }}
        >
          <span>Boutique</span>
          <span>One-shot {ONESHOT_PRICE}$</span>
          <span>Maintenance</span>
          <span>Utilisateurs supp.</span>
          <span>Total mensuel</span>
          <span></span>
        </div>

        <div className="divide-y" style={{ borderColor: COLORS.border }}>
          {boutiques.map((b, index) => {
            const extraUsers = Math.max(0, b.users - 1);
            const extraCost = extraUsers * EXTRA_USER_PRICE;
            const totalMensuel = MAINTENANCE_PRICE + extraCost;

            return (
              <motion.div
                key={b.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: index * 0.03 }}
                className="lg:grid lg:grid-cols-[2fr,1fr,1fr,1fr,1fr,60px] gap-4 p-4 lg:px-6 lg:py-4 items-center hover:bg-slate-50 transition-colors"
              >
                {/* Boutique */}
                <div className="flex items-center gap-3 mb-3 lg:mb-0">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center text-white font-bold text-xs" style={{ backgroundColor: COLORS.accent }}>
                    {b.name.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase()}
                  </div>
                  <div>
                    <p className="font-medium text-sm" style={{ color: COLORS.text }}>{b.name}</p>
                    <p className="text-xs" style={{ color: COLORS.textSecondary }}>{b.users} utilisateur{b.users > 1 ? 's' : ''}</p>
                  </div>
                </div>

                {/* One-shot */}
                <div className="flex items-center gap-2 mb-2 lg:mb-0">
                  <span className="lg:hidden text-xs" style={{ color: COLORS.textSecondary }}>One-shot:</span>
                  {b.oneshotPaid ? (
                    <div className="flex items-center gap-1.5">
                      <CheckCircle className="w-4 h-4" style={{ color: COLORS.positive }} />
                      <span className="text-sm" style={{ color: COLORS.positive }}>Payé {b.oneshotDate ? `le ${format(new Date(b.oneshotDate), "dd/MM/yy")}` : ''}</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <XCircle className="w-4 h-4" style={{ color: COLORS.negative }} />
                      <span className="text-sm" style={{ color: COLORS.negative }}>Non payé</span>
                      <button
                        onClick={() => markOneshotPaid(b)}
                        className="text-xs font-medium px-3 py-1 rounded-lg text-white"
                        style={{ backgroundColor: COLORS.accent }}
                        data-testid={`mark-oneshot-${b.id}`}
                      >
                        Marquer payé
                      </button>
                    </div>
                  )}
                </div>

                {/* Maintenance */}
                <div className="flex items-center gap-2 mb-2 lg:mb-0">
                  <span className="lg:hidden text-xs" style={{ color: COLORS.textSecondary }}>Maintenance:</span>
                  {b.maintenancePaid ? (
                    <div className="flex items-center gap-1.5">
                      <CheckCircle className="w-4 h-4" style={{ color: COLORS.positive }} />
                      <span className="text-sm" style={{ color: COLORS.positive }}>Payé</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" style={{ color: COLORS.warning }} />
                      <span className="text-sm" style={{ color: COLORS.warning }}>En attente</span>
                      <button
                        onClick={() => markMaintenancePaid(b)}
                        className="text-xs font-medium px-3 py-1 rounded-lg text-white"
                        style={{ backgroundColor: COLORS.accent }}
                        data-testid={`mark-maintenance-${b.id}`}
                      >
                        Marquer payé
                      </button>
                    </div>
                  )}
                </div>

                {/* Utilisateurs supp. */}
                <div className="flex items-center gap-2 mb-2 lg:mb-0">
                  <span className="lg:hidden text-xs" style={{ color: COLORS.textSecondary }}>Supp.:</span>
                  {extraUsers > 0 ? (
                    <span className="text-sm" style={{ color: COLORS.warning }}>
                      {extraUsers} supp. = +{extraCost}$/mois
                    </span>
                  ) : (
                    <span className="text-sm" style={{ color: COLORS.textSecondary }}>—</span>
                  )}
                </div>

                {/* Total mensuel */}
                <div className="flex items-center gap-2 mb-2 lg:mb-0">
                  <span className="lg:hidden text-xs" style={{ color: COLORS.textSecondary }}>Total:</span>
                  <span className="font-bold" style={{ color: COLORS.text }}>{totalMensuel}$/mois</span>
                </div>

                {/* Actions */}
                <div className="flex justify-end">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreVertical className="w-4 h-4" style={{ color: COLORS.textSecondary }} />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56" style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}>
                      {!b.oneshotPaid && (
                        <DropdownMenuItem style={{ color: COLORS.text }} onClick={() => markOneshotPaid(b)}>
                          <DollarSign className="w-4 h-4 mr-2" />
                          Marquer one-shot payé
                        </DropdownMenuItem>
                      )}
                      {!b.maintenancePaid && (
                        <DropdownMenuItem style={{ color: COLORS.text }} onClick={() => markMaintenancePaid(b)}>
                          <Repeat className="w-4 h-4 mr-2" />
                          Marquer maintenance payée
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator style={{ backgroundColor: COLORS.border }} />
                      <DropdownMenuItem style={{ color: COLORS.text }} onClick={() => setHistoryModal({ open: true, boutique: b })}>
                        <FileText className="w-4 h-4 mr-2" />
                        Voir historique paiements
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Card>

      {/* Modales */}
      <PaymentModal
        isOpen={paymentModal.open}
        onClose={() => setPaymentModal({ open: false, boutique: null, type: "one-shot" })}
        boutique={paymentModal.boutique}
        paymentType={paymentModal.type}
        onSave={handleSavePayment}
      />

      <HistoryModal
        isOpen={historyModal.open}
        onClose={() => setHistoryModal({ open: false, boutique: null })}
        boutique={historyModal.boutique}
        payments={payments}
      />
    </div>
  );
}
